import { FC, useCallback, useEffect } from "react";
import {
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Tooltip,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useEffectOnceWhen } from "rooks";
import type { CustomHeaderProps } from "ag-grid-react";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { setGridResourceMetricFrequency } from "@/store/resources-grid/resourcesGridSlice";
import { resourceGridMetricFrequencySelector } from "@/store/resources-grid/selectors/grid/resourceGridMetricFrequencySelector";
import { resourceMetricsColumnDefFieldName } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/getResourceMetricsColumnDef";
import { metricsChartsPeriods } from "@/components/pages/common/resource-details/utils/constants/metrics";
import { metricsFrequencies } from "@/components/pages/common/resources-ag-grid/utils/metrics/metricsConstants";
import { MetricsKey } from "@/components/pages/common/resources-ag-grid/utils/metrics/metricsTypes";

export const ResourceMetricsHeaderComponent: FC<CustomHeaderProps> = ({
  displayName,
  innerHeaderComponentParams,
  api,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const tooltip = props.column.getColDef().headerTooltip;
  const masterGridId = props.context?.masterGridId;

  const { resourceType, metricKey } = innerHeaderComponentParams;

  const metricFrequencyByDefault = metricsFrequencies[metricKey as MetricsKey];

  const frequency = useAppSelector((state) =>
    resourceGridMetricFrequencySelector(
      state,
      resourceType,
      metricKey,
      masterGridId,
    ),
  );

  const dispatcher = useCallback(
    (frequency: string) => {
      if (!masterGridId) {
        return;
      }

      dispatch(
        setGridResourceMetricFrequency({
          frequency,
          metricKey,
          resourceType,
          gridId: masterGridId,
        }),
      );
    },
    [dispatch, metricKey, masterGridId, resourceType],
  );

  const changeHandler = useCallback(
    (event: SelectChangeEvent) => {
      dispatcher(event.target.value);
    },
    [dispatcher],
  );

  const metricsKeys = Object.keys(metricsChartsPeriods);

  useEffectOnceWhen(() => {
    const defaultKey = metricsKeys.at(1);
    if (defaultKey) {
      dispatcher(defaultKey);
    }
  }, !frequency && !metricFrequencyByDefault);

  useEffectOnceWhen(() => {
    if (metricFrequencyByDefault) {
      dispatcher(metricFrequencyByDefault);
    }
  }, !!metricFrequencyByDefault);

  useEffect(() => {
    const defaultSortModel = api.getState()?.sort?.sortModel?.at(0);
    const defaultSort = defaultSortModel?.sort ?? "asc";
    const defaultSortColId = defaultSortModel?.colId;
    const sortedByMetrics =
      defaultSortColId === resourceMetricsColumnDefFieldName;

    if (sortedByMetrics) {
      api.applyColumnState({
        state: [{ colId: resourceMetricsColumnDefFieldName, sort: null }],
        defaultState: { sort: null },
      });

      setTimeout(() => {
        api.applyColumnState({
          state: [
            { colId: resourceMetricsColumnDefFieldName, sort: defaultSort },
          ],
          defaultState: { sort: null },
        });
      });
    }
  }, [api, props.column, frequency]);

  if (!frequency) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <TypographyWithTooltip
        title={displayName}
        variant="inherit"
        fontWeight="inherit"
      />

      {tooltip && (
        <Tooltip arrow title={tooltip} placement="top">
          <HelpOutlineIcon
            fontSize="small"
            color="info"
            sx={{ verticalAlign: "middle", ml: 1 }}
          />
        </Tooltip>
      )}

      <FormControl size="xsmall" focused={false} sx={{ minWidth: 57 }}>
        <Select
          value={frequency}
          onChange={changeHandler}
          sx={{ "& .MuiSelect-select": { pl: 0.5 } }}
        >
          {metricsKeys.map((key) => {
            return (
              <MenuItem value={key} key={key}>
                {key}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Stack>
  );
};
